import { TDocument } from '../documents/documents.interfaces';
import {
  TActions,
  TPaginatedResponse,
  TPaginationParams,
} from '../__shared/shared.interfaces';

export enum TStatus {
  PAID = 'paid',
  PARTIAL = 'partial',
  UNPAID = 'unpaid',
  N_A = 'n/a',
}

export enum TPaymentMode {
  CASH = 'cash',
  BANK = 'bank',
  CARD = 'card',
  ONLINE = 'online',
  CHEQUE = 'cheque',
  BANK_TRANSFER = 'bank_transfer',
  N_A = 'n/a',
}

export enum TTransactionType {
  ALL = 'all',
  FEES = 'fees',
  INCOME = 'income',
  EXPENSE = 'expense',
}

export enum TTransactionCategory {
  FEES = 'fees',
  SALARY = 'salary',
  EXPENSE = 'expense',
}

export enum TTransactionCurrency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  INR = 'INR',
}

export interface TFinancePrimary {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency?: TTransactionCurrency;
  currencyCode?: TTransactionCurrency;
  date: Date;
  dueDate?: Date;
  tags?: string[];
  type: TTransactionType;
  category: TTransactionCategory;
  attachments?: TDocument[];
  status: TStatus;
  paymentMode: TPaymentMode;
  paidAmount?: number;
  academicYear?: number;
  actions: TActions;
}

export interface TInstituteAddress {
  _id: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  pinCode: string;
  isCurrent: boolean;
  description: string;
}

export interface TInstituteContact {
  _id: string;
  phone: {
    country_code: string;
    phone_number: string;
    is_verified: boolean;
  };
  email: {
    email: string;
    is_verified: boolean;
  };
}

export interface CreatedBy {
  id: string;
  name: string;
  userRole: string;
  createdAt: Date;
}

export interface Institute {
  address: TInstituteAddress;
  contact: TInstituteContact;
}

export interface TFinance {
  id: string;
  amount: number;
  currencyCode: TTransactionCurrency;
  description: string;
  fine: number;
  fineCurrencyCode: TTransactionCurrency;
  instituteId: string;
  isCredit: boolean;
  tags?: string[];
  type: string;
  category: TTransactionCategory;
  counterRef?: string;
  counterRefType?: string;
  academicYear: number;
  dueDate: Date;
  purpose: string;
  invoiceKey: string;
  createdBy: CreatedBy;
  createdAt: Date;
}

export interface TFinanceRecordData extends AuditFields {
  _id: string;
  amount: number;
  currencyCode: TTransactionCurrency;
  description: string;
  fine: number;
  fineCurrencyCode: TTransactionCurrency;
  institute: Institute;
  isCredit: boolean;
  purpose: string;
}

export interface AuditFields {
  createdBy: CreatedBy;
  createdAt: Date;
  updatedBy?: string;
  updatedAt?: Date;
}

export type TFinanceApiResponse = TPaginatedResponse<TFinancePrimary>;

// ---------------- Finance List Query Params -----------------
export interface FinanceListParams extends TPaginationParams {
  type?: TTransactionType | string;
  status?: TStatus | string;
  category?: TTransactionCategory | string;
  paymentMode?: TPaymentMode | string;
  academicYear?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ---------------- Finance Create/Update Request -----------------
export interface TFinanceCreateRequest {
  amount: number;
  currencyCode: TTransactionCurrency;
  description: string;
  fine?: number;
  fineCurrencyCode?: TTransactionCurrency;
  instituteId: string;
  isCredit: boolean;
  tags?: string[];
  type: TTransactionType | string;
  category: TTransactionCategory;
  counterRef?: string;
  counterRefType?: string;
  academicYear: number;
  dueDate: Date | string;
  purpose: string;
  invoiceKey?: string;
}

export interface TFinanceUpdateRequest extends TFinanceCreateRequest {
  id: string;
}

export enum FEE_PAYMENT_SCHEDULE {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  SEMI_ANNUAL = 'semi_annual',
  ANNUAL = 'annual',
}

// ---------------- Finance Details Endpoint Interfaces -----------------
export interface TFinanceDetails {
  basic_details: TFinanceBasicDetails;
  institute: TFinanceInstitute;
  counter_ref?: TFinanceCounterRef;
  students: TFinanceStudent[];
  fee_schedule?: TFinanceFeeSchedule;
  documents: TFinanceDocument[];
  action: TFinanceUserAction;
}

export interface TFinanceBasicDetails {
  _id: string;
  amount: number;
  paid_amount?: number;
  currency_code: string; // CurrencyTypes enum
  description: string;
  fine?: number;
  fine_currency_code?: string;
  is_credit?: boolean;
  academic_year: number;
  due_date?: Date;
  payment_mode?: string; // PaymentMode enum
  status?: string; // 'paid' | 'partial' | 'unpaid'
  type: string; // CashLedgerPurpose enum
}

export interface TFinanceInstitute {
  _id: string;
  name: string;
}

export interface TFinanceCounterRef {
  counter_ref_type?: string; // 'event' | 'inventory' | 'fee_schedule'
  counter_ref_id?: string;
  counter_ref_details?: any; // Type-specific details based on counter_ref_type
}

export interface TFinanceStudent {
  _id: string;
  first_name: string;
  last_name: string;
  enrollment_date?: Date;
  academic_year?: number;
}

export interface TFinanceFeeSchedule {
  _id?: string;
  original_amount?: number;
  adjusted_amount?: number;
  payable_amount?: number;
  status?: string; // FeeScheduleStatus enum
}

export interface TFinanceDocument {
  _id: string;
  name: string;
  document_type: string;
  status: string;
  file_key: string;
}

export interface TFinanceUserAction {
  created_at: Date;
  created_by: string;
  updated_at: Date;
  updated_by: string;
}
